import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import { supabaseBrowser } from './supabaseClient';

const SUCCESS_DISMISS_MS = 8000;

const AUTH_TOKEN_KEY = 'clientflow_auth_token';

/** API + widget script base (same host as /widget.js). Set REACT_APP_API_URL in Vercel or .env. */
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

/** Optional: load the real public widget on the sign-in page (same as embed on any site). */
const LOGIN_WIDGET_PUBLIC_KEY = (process.env.REACT_APP_LOGIN_WIDGET_PUBLIC_KEY || '').trim();

const PROJECTS_URL = `${API_BASE_URL}/projects`;

function getAuthToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function saveAuthToken(token) {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // ignore
  }
}

function apiFetch(url, options = {}) {
  const headers = { ...(options.headers || {}) };
  const t = getAuthToken();
  if (t) headers.Authorization = `Bearer ${t}`;
  return fetch(url, { ...options, headers });
}

function buildEmbedScript(publicKey) {
  const base = API_BASE_URL;
  return `<script>
  window.AI_CONFIG = {
    publicKey: "${publicKey}",
    apiUrl: "${base}"
  };
</script>
<script src="${base}/widget.js"></script>`;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

/** Read email claim from Supabase JWT. */
function emailFromJwt(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4;
    if (pad) payload += '='.repeat(4 - pad);
    const json = JSON.parse(atob(payload));
    return json.email || json.user_metadata?.email || null;
  } catch {
    return null;
  }
}

export default function App() {
  const [tokenPresent, setTokenPresent] = useState(() => !!getAuthToken());
  /** False until first Supabase getSession completes (avoids login flash when session restores). */
  const [authReady, setAuthReady] = useState(() => !supabaseBrowser);
  const [authBusy, setAuthBusy] = useState(false);

  const [projects, setProjects] = useState([]);
  const [createAssistantOpen, setCreateAssistantOpen] = useState(false);
  const [createFormError, setCreateFormError] = useState('');
  /** Initial / refetch GET /projects */
  const [projectsLoading, setProjectsLoading] = useState(() => !!getAuthToken());
  /** POST /projects in progress */
  const [savingProject, setSavingProject] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [copyToast, setCopyToast] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  /** { type: 'website' | 'etsy', project } | null */
  const [assistantModal, setAssistantModal] = useState(null);
  const [modalCopyDone, setModalCopyDone] = useState(false);

  const [newName, setNewName] = useState('');
  const [newTone, setNewTone] = useState('friendly');
  const [newNotes, setNewNotes] = useState('');

  const [editAssistantOpen, setEditAssistantOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editTone, setEditTone] = useState('friendly');
  const [editStyle, setEditStyle] = useState('normal');
  const [editNotes, setEditNotes] = useState('');
  const [editFormError, setEditFormError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!getAuthToken()) {
      setProjects([]);
      setProjectsLoading(false);
      return;
    }
    setProjectsLoading(true);
    setError('');
    try {
      const res = await apiFetch(PROJECTS_URL);
      const data = await res.json();
      if (res.status === 401) {
        saveAuthToken('');
        setTokenPresent(false);
        throw new Error('Session expired. Please sign in again.');
      }
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tokenPresent) loadProjects();
  }, [tokenPresent, loadProjects]);

  useEffect(() => {
    if (!tokenPresent) {
      setUserEmail('');
      return;
    }
    let cancelled = false;
    (async () => {
      if (supabaseBrowser) {
        const { data: { user } } = await supabaseBrowser.auth.getUser();
        if (!cancelled && user?.email) {
          setUserEmail(user.email);
          return;
        }
      }
      const em = emailFromJwt(getAuthToken());
      if (!cancelled) setUserEmail(em || '');
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenPresent]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const t = setTimeout(() => setSuccessMessage(''), SUCCESS_DISMISS_MS);
    return () => clearTimeout(t);
  }, [successMessage]);

  /** Supabase session → JWT in localStorage for API; optional Google OAuth. */
  useEffect(() => {
    if (!supabaseBrowser) return undefined;

    const syncFromSession = (session) => {
      if (session?.access_token) {
        saveAuthToken(session.access_token);
        setTokenPresent(true);
        setProjectsLoading(true);
      }
      if (session?.user?.email) setUserEmail(session.user.email);
    };

    supabaseBrowser.auth
      .getSession()
      .then(({ data: { session } }) => {
        syncFromSession(session);
      })
      .finally(() => {
        setAuthReady(true);
      });

    const {
      data: { subscription },
    } = supabaseBrowser.auth.onAuthStateChange((event, session) => {
      if (session?.access_token) {
        syncFromSession(session);
      }
      if (event === 'SIGNED_OUT') {
        saveAuthToken('');
        setUserEmail('');
        setTokenPresent(false);
        setProjects([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    if (!supabaseBrowser) return;
    setAuthBusy(true);
    setError('');
    try {
      const redirectTo = `${window.location.origin}${window.location.pathname || '/'}`;
      const { data, error: oauthErr } = await supabaseBrowser.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      });
      if (oauthErr) throw oauthErr;
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error('No OAuth URL returned');
    } catch (e) {
      setError(e.message || 'Google sign-in failed');
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (supabaseBrowser) await supabaseBrowser.auth.signOut();
    } catch (e) {
      console.warn(e);
    }
    saveAuthToken('');
    setTokenPresent(false);
    setProjects([]);
    setCreateAssistantOpen(false);
    setEditAssistantOpen(false);
    setEditingProject(null);
    setError('');
    setSuccessMessage('');
    setCopyToast(false);
    setAssistantModal(null);
    setUserEmail('');
  };

  const openCreateAssistantModal = () => {
    setNewName('');
    setNewTone('friendly');
    setNewNotes('');
    setCreateFormError('');
    setSuccessMessage('');
    setCreateAssistantOpen(true);
  };

  const openEditAssistant = (p) => {
    const r = p.rules && typeof p.rules === 'object' ? p.rules : {};
    const tone = typeof r.tone === 'string' ? r.tone.trim() : '';
    const style = typeof r.style === 'string' ? r.style.trim() : '';
    setEditTone(['friendly', 'professional', 'casual'].includes(tone) ? tone : 'friendly');
    setEditStyle(['short', 'normal', 'detailed'].includes(style) ? style : 'normal');
    setEditNotes(typeof r.notes === 'string' ? r.notes : '');
    setEditingProject(p);
    setEditFormError('');
    setSuccessMessage('');
    setEditAssistantOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!editingProject) return;
    setSavingEdit(true);
    setEditFormError('');
    try {
      const res = await apiFetch(`${PROJECTS_URL}/${encodeURIComponent(editingProject.name)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rules: {
            tone: editTone,
            style: editStyle,
            notes: editNotes.trim(),
          },
        }),
      });
      const data = await res.json();
      if (res.status === 401) {
        saveAuthToken('');
        setTokenPresent(false);
        throw new Error('Session expired.');
      }
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setEditAssistantOpen(false);
      setEditingProject(null);
      setSuccessMessage(`Assistant “${data.name || editingProject.name}” was updated.`);
      await loadProjects();
    } catch (err) {
      setEditFormError(err.message || 'Failed to update assistant');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) {
      setCreateFormError('Name is required.');
      return;
    }
    setSavingProject(true);
    setCreateFormError('');
    try {
      const res = await apiFetch(PROJECTS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          rules: {
            tone: newTone,
            style: 'normal',
            notes: newNotes.trim(),
          },
        }),
      });
      const data = await res.json();
      if (res.status === 401) {
        saveAuthToken('');
        setTokenPresent(false);
        throw new Error('Session expired.');
      }
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setCreateAssistantOpen(false);
      setNewName('');
      setNewTone('friendly');
      setNewNotes('');
      setSuccessMessage(
        `Assistant “${data.name || name}” was created. Use “Use on Website” to get the embed code.`
      );
      await loadProjects();
    } catch (e) {
      setCreateFormError(e.message || 'Failed to create assistant');
    } finally {
      setSavingProject(false);
    }
  };

  const handleCopyEmbedFromModal = async (project) => {
    const pk = project.public_key;
    if (!pk) {
      setError('This assistant has no embed key yet.');
      return;
    }
    const ok = await copyToClipboard(buildEmbedScript(pk));
    if (ok) {
      setModalCopyDone(true);
      setCopyToast(true);
      setTimeout(() => {
        setModalCopyDone(false);
        setCopyToast(false);
      }, 2500);
    } else {
      setError('Could not copy to clipboard.');
    }
  };

  useEffect(() => {
    if (!assistantModal && !createAssistantOpen && !editAssistantOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (savingProject || savingEdit) return;
      if (createAssistantOpen) setCreateAssistantOpen(false);
      else if (editAssistantOpen) {
        setEditAssistantOpen(false);
        setEditingProject(null);
      } else setAssistantModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [assistantModal, createAssistantOpen, editAssistantOpen, savingProject, savingEdit]);

  /** Real embed: same widget.js + /chat-public as production sites; removed when user signs in. */
  useEffect(() => {
    if (tokenPresent || !authReady) return undefined;
    if (!LOGIN_WIDGET_PUBLIC_KEY || !API_BASE_URL) return undefined;

    window.AI_CONFIG = {
      publicKey: LOGIN_WIDGET_PUBLIC_KEY,
      apiUrl: API_BASE_URL,
    };

    const script = document.createElement('script');
    script.src = `${API_BASE_URL}/widget.js`;
    script.async = true;
    script.dataset.clientflowWidgetLoader = '1';
    document.body.appendChild(script);

    return () => {
      script.remove();
      try {
        delete window.AI_CONFIG;
      } catch {
        window.AI_CONFIG = undefined;
      }
      const root = document.querySelector('[data-ai-widget]');
      if (root?.parentNode) root.parentNode.removeChild(root);
    };
  }, [tokenPresent, authReady]);

  const authFooter = (
    <footer className="dash-auth-site-footer">
      <p className="dash-auth-footer-email">
        Email Support:{' '}
        <a href="mailto:support@pluto3d.app">support@pluto3d.app</a>
      </p>
      <nav className="dash-auth-footer-nav" aria-label="Legal and support">
        <button type="button" className="dash-auth-footer-link">
          Plans
        </button>
        <span className="dash-auth-footer-sep" aria-hidden="true">
          |
        </span>
        <button type="button" className="dash-auth-footer-link">
          Privacy
        </button>
        <span className="dash-auth-footer-sep" aria-hidden="true">
          |
        </span>
        <button type="button" className="dash-auth-footer-link">
          Terms
        </button>
        <span className="dash-auth-footer-sep" aria-hidden="true">
          |
        </span>
        <a href="mailto:support@pluto3d.app">Support</a>
      </nav>
    </footer>
  );

  if (!tokenPresent) {
    if (!authReady) {
      return (
        <div className="dash dash-auth-page">
          <div className="dash-auth-landing dash-auth-landing--loading" aria-busy="true" aria-label="Checking session">
            <div className="dash-auth-bootstrap">
              <div className="dash-spinner" />
            </div>
          </div>
          {authFooter}
        </div>
      );
    }

    return (
      <div className="dash dash-auth-page">
        <div className="dash-auth-landing">
          <div className="dash-auth-card dash-auth-card--saas">
            <h1 className="dash-auth-card-title">Pluto Assist</h1>
            <p className="dash-auth-card-subtitle">Create your AI assistant for Etsy or your website</p>
            <p className="dash-auth-description">
              Test the AI assistant using the chat bubble, then continue with Google to manage your assistants.
            </p>
            {error && <p className="dash-error dash-auth-card-error">{error}</p>}
            {supabaseBrowser ? (
              <button
                type="button"
                className="dash-btn dash-btn-google-primary"
                onClick={handleGoogleLogin}
                disabled={authBusy}
              >
                {authBusy ? 'Redirecting…' : 'Continue with Google'}
              </button>
            ) : (
              <p className="dash-auth-config-msg">
                Sign-in is not available: set <code>REACT_APP_SUPABASE_URL</code> and{' '}
                <code>REACT_APP_SUPABASE_ANON_KEY</code> in the deployment environment.
              </p>
            )}
          </div>
        </div>
        {authFooter}
      </div>
    );
  }

  return (
    <div className="dash dash-app">
      <header className="dash-bar dash-bar-app">
        <span className="dash-brand">Pluto Assist</span>
        <div className="dash-bar-right">
          {userEmail ? (
            <span className="dash-user-email" title={userEmail}>
              {userEmail}
            </span>
          ) : (
            <span className="dash-user-email dash-user-email--muted">Signed in</span>
          )}
          <button type="button" className="dash-btn dash-btn-outline" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </header>

      <main className="dash-main dash-main-wide">
        <div className="dash-head">
          <div className="dash-head-title">
            <h1 className="dash-page-title">Your AI Assistants</h1>
            {projectsLoading && projects.length > 0 && (
              <span className="dash-updating" aria-live="polite">
                <span className="dash-spinner dash-spinner--inline" aria-hidden />
                Updating…
              </span>
            )}
          </div>
          <button type="button" className="dash-btn dash-btn-primary dash-btn-create" onClick={openCreateAssistantModal}>
            + Create Assistant
          </button>
        </div>

        {copyToast && (
          <div className="dash-toast dash-toast--copy" role="status">
            Copied! Embed script is on your clipboard.
          </div>
        )}
        {successMessage && (
          <div className="dash-toast dash-toast--success" role="status">
            {successMessage}
          </div>
        )}
        {error && <p className="dash-error">{error}</p>}

        {projectsLoading && projects.length === 0 ? (
          <div className="dash-loading" aria-busy="true" aria-label="Loading assistants">
            <div className="dash-spinner" />
            <p className="dash-loading-text">Loading assistants…</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="dash-empty">
            <p className="dash-empty-title">Create your first AI assistant</p>
            <p className="dash-empty-hint">
              Add an assistant to get an embed script for your website and use the same setup in the Etsy extension.
            </p>
            <button type="button" className="dash-btn dash-btn-primary" onClick={openCreateAssistantModal}>
              + Create Assistant
            </button>
          </div>
        ) : (
          <ul className={`dash-cards${projectsLoading ? ' dash-cards--dim' : ''}`}>
            {projects.map((p) => (
              <li key={p.id || p.name} className="dash-card dash-card-saas">
                <div className="dash-card-head-row">
                  <h2 className="dash-card-title dash-card-title--inline">{p.name}</h2>
                  <button
                    type="button"
                    className="dash-btn dash-btn-secondary dash-btn-edit-inline"
                    onClick={() => openEditAssistant(p)}
                  >
                    Edit
                  </button>
                </div>
                <div className="dash-card-actions">
                  <button
                    type="button"
                    className="dash-btn dash-btn-secondary"
                    onClick={() => {
                      setModalCopyDone(false);
                      setAssistantModal({ type: 'website', project: p });
                    }}
                  >
                    Use on Website
                  </button>
                  <button
                    type="button"
                    className="dash-btn dash-btn-outline"
                    onClick={() => setAssistantModal({ type: 'etsy', project: p })}
                  >
                    Use on Etsy
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {editAssistantOpen && editingProject && (
        <div
          className="dash-modal-root dash-modal-root--create"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-assistant-modal-title"
        >
          <button
            type="button"
            className="dash-modal-backdrop"
            aria-label="Close"
            onClick={() => {
              if (!savingEdit) {
                setEditAssistantOpen(false);
                setEditingProject(null);
              }
            }}
          />
          <div className="dash-modal dash-modal--create">
            <h2 id="edit-assistant-modal-title" className="dash-modal-title">
              Edit assistant
            </h2>
            <p className="dash-modal-meta">
              Assistant: <span className="dash-modal-readonly-name">{editingProject.name}</span>
            </p>
            <form className="dash-modal-form" onSubmit={handleUpdateProject}>
              <div className="dash-field">
                <label htmlFor="edit-tone">Tone</label>
                <select
                  id="edit-tone"
                  value={editTone}
                  onChange={(e) => setEditTone(e.target.value)}
                  disabled={savingEdit}
                >
                  <option value="friendly">friendly</option>
                  <option value="professional">professional</option>
                  <option value="casual">casual</option>
                </select>
              </div>
              <div className="dash-field">
                <label htmlFor="edit-style">Style</label>
                <select
                  id="edit-style"
                  value={editStyle}
                  onChange={(e) => setEditStyle(e.target.value)}
                  disabled={savingEdit}
                >
                  <option value="short">short</option>
                  <option value="normal">normal</option>
                  <option value="detailed">detailed</option>
                </select>
              </div>
              <div className="dash-field">
                <label htmlFor="edit-notes">Notes</label>
                <textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Optional instructions for the AI…"
                  rows={4}
                  disabled={savingEdit}
                />
              </div>
              {editFormError && <p className="dash-error dash-modal-error">{editFormError}</p>}
              <div className="dash-modal-footer">
                <button
                  type="button"
                  className="dash-btn dash-btn-secondary"
                  onClick={() => {
                    if (!savingEdit) {
                      setEditAssistantOpen(false);
                      setEditingProject(null);
                    }
                  }}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-primary" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {createAssistantOpen && (
        <div
          className="dash-modal-root dash-modal-root--create"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-assistant-modal-title"
        >
          <button
            type="button"
            className="dash-modal-backdrop"
            aria-label="Close"
            onClick={() => !savingProject && setCreateAssistantOpen(false)}
          />
          <div className="dash-modal dash-modal--create">
            <h2 id="create-assistant-modal-title" className="dash-modal-title">
              Create Assistant
            </h2>
            <p className="dash-modal-meta">Define how this assistant responds for your shop.</p>
            <form className="dash-modal-form" onSubmit={handleCreateProject}>
              <div className="dash-field">
                <label htmlFor="create-name">Name</label>
                <input
                  id="create-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. my-shop-assistant"
                  disabled={savingProject}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="dash-field">
                <label htmlFor="create-tone">Tone</label>
                <select
                  id="create-tone"
                  value={newTone}
                  onChange={(e) => setNewTone(e.target.value)}
                  disabled={savingProject}
                >
                  <option value="friendly">friendly</option>
                  <option value="professional">professional</option>
                  <option value="casual">casual</option>
                </select>
              </div>
              <div className="dash-field">
                <label htmlFor="create-notes">Notes</label>
                <textarea
                  id="create-notes"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="Optional instructions for the AI…"
                  rows={4}
                  disabled={savingProject}
                />
              </div>
              {createFormError && <p className="dash-error dash-modal-error">{createFormError}</p>}
              <div className="dash-modal-footer">
                <button
                  type="button"
                  className="dash-btn dash-btn-secondary"
                  onClick={() => !savingProject && setCreateAssistantOpen(false)}
                  disabled={savingProject}
                >
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-primary" disabled={savingProject}>
                  {savingProject ? 'Creating…' : 'Create assistant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {assistantModal && (
        <div className="dash-modal-root" role="dialog" aria-modal="true" aria-labelledby="assistant-modal-title">
          <button
            type="button"
            className="dash-modal-backdrop"
            aria-label="Close"
            onClick={() => setAssistantModal(null)}
          />
          <div className="dash-modal">
            {assistantModal.type === 'website' && (
              <>
                <h2 id="assistant-modal-title" className="dash-modal-title">
                  Embed on your website
                </h2>
                <p className="dash-modal-meta">Assistant: {assistantModal.project.name}</p>
                <label className="dash-modal-label" htmlFor="embed-code">
                  Paste this before your closing <code>&lt;/body&gt;</code> tag:
                </label>
                <textarea
                  id="embed-code"
                  className="dash-modal-code"
                  readOnly
                  value={
                    assistantModal.project.public_key
                      ? buildEmbedScript(assistantModal.project.public_key)
                      : 'No embed key — run database migration or recreate this assistant.'
                  }
                  rows={10}
                />
                <div className="dash-modal-footer">
                  <button
                    type="button"
                    className="dash-btn dash-btn-secondary"
                    onClick={() => setAssistantModal(null)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className={`dash-btn dash-btn-primary${modalCopyDone ? ' dash-btn--copied' : ''}`}
                    onClick={() => handleCopyEmbedFromModal(assistantModal.project)}
                    disabled={!assistantModal.project.public_key}
                  >
                    {modalCopyDone ? 'Copied!' : 'Copy to clipboard'}
                  </button>
                </div>
              </>
            )}
            {assistantModal.type === 'etsy' && (
              <>
                <h2 id="assistant-modal-title" className="dash-modal-title">
                  Use on Etsy
                </h2>
                <p className="dash-modal-body">
                  This assistant is ready to use in the Etsy extension.
                </p>
                <div className="dash-modal-footer dash-modal-footer--single">
                  <button type="button" className="dash-btn dash-btn-primary" onClick={() => setAssistantModal(null)}>
                    Got it
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
